import { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard, SectionTitle } from "@/components/GlassBits";
import { formatINR, formatDate, statusBadgeClass } from "@/lib/format";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend
} from "recharts";

const CHART_COLORS = ["#fbce18", "#10b981", "#3b82f6", "#a855f7", "#ef4444"];

export default function CustomerDashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [tx, setTx] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, c, t] = await Promise.all([
          api.get("/customer/dashboard/stats"),
          api.get("/customer/charts"),
          api.get("/customer/transactions"),
        ]);
        if (cancelled) return;
        setStats(s.data); setCharts(c.data); setTx(t.data.slice(0, 5));
      } catch (e) {
        console.error("[CustomerDashboard] load failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!stats) return <div className="text-white/50">Loading…</div>;

  return (
    <div>
      <div className="mt-2">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#fbce18]/80">Your account</div>
        <h1 className="mt-1 font-display text-3xl tracking-tight">Overview</h1>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="cstat-spent" label="Lifetime Spent" value={formatINR(stats.lifetime_spent)} sub={`${stats.total_transactions} transactions`} accent />
        <StatCard testid="cstat-pending" label="Pending" value={formatINR(stats.pending_amount)} sub={`${stats.pending_count} invoices`} />
        <StatCard testid="cstat-invoices" label="Total Invoices" value={stats.total_invoices} />
        <StatCard testid="cstat-last" label="Last Payment" value={stats.last_payment_date ? formatDate(stats.last_payment_date) : "—"} />
      </div>

      <div className="mt-4 grid sm:grid-cols-3 gap-4">
        <StatCard testid="cstat-avg" label="Average Payment" value={formatINR(stats.average_payment)} />
        <StatCard testid="cstat-favmethod" label="Favourite Method" value={(stats.favorite_method || "—").toUpperCase()} />
        <StatCard testid="cstat-favbranch" label="Most Paid Branch" value={stats.favorite_branch || "—"} />
      </div>

      {charts && (
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          <div className="ldc-glass p-5">
            <SectionTitle eyebrow="Spending" title="Monthly graph" />
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.monthly}>
                  <defs>
                    <linearGradient id="cg1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fbce18" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#fbce18" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" stroke="rgba(255,255,255,0.4)" fontSize={11} />
                  <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} />
                  <Tooltip contentStyle={{ background: "rgba(15,15,15,0.95)", border: "1px solid rgba(251,206,24,0.3)", borderRadius: 8 }} />
                  <Area type="monotone" dataKey="amount" stroke="#fbce18" strokeWidth={2} fill="url(#cg1)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="ldc-glass p-5">
            <SectionTitle eyebrow="Categories" title="Category spend" />
            {charts.categories.length === 0 ? (
              <div className="text-sm text-white/40 py-10 text-center">No spend yet</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.categories}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="branch" stroke="rgba(255,255,255,0.4)" fontSize={11} />
                    <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} />
                    <Tooltip contentStyle={{ background: "rgba(15,15,15,0.95)", border: "1px solid rgba(251,206,24,0.3)", borderRadius: 8 }} />
                    <Bar dataKey="total" fill="#fbce18" radius={[6,6,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 p-4 rounded-lg border border-white/8 bg-white/[0.03] flex items-start gap-3" data-testid="customer-refund-policy">
        <div className="w-8 h-8 rounded-md bg-[#fbce18]/10 border border-[#fbce18]/30 flex items-center justify-center text-[#fbce18] shrink-0 mt-0.5" style={{fontSize: 14, fontWeight: 600}}>i</div>
        <div className="text-xs text-white/60 leading-relaxed">
          <span className="text-[#fbce18] font-semibold uppercase tracking-[0.14em] text-[10px]">Refund policy · </span>
          Refunds are available only within <span className="text-white/90 font-semibold">2 days (48 hours)</span> of successful payment and are subject to admin approval. Contact your branch admin to request a refund.
        </div>
      </div>

      <div className="mt-8">
        <SectionTitle eyebrow="Recent" title="Latest transactions" />
        <div className="ldc-glass overflow-hidden">
          <div className="overflow-x-auto ldc-scroll">
            <table className="w-full ldc-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Purpose</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {tx.length === 0 && (<tr><td colSpan={5} className="text-center py-10 text-white/40">No transactions yet.</td></tr>)}
                {tx.map((t) => (
                  <tr key={(t.payment_id || t.invoice_no) + t.created_at} data-testid={`ctx-row-${t.invoice_no}`}>
                    <td className="font-mono text-[#fbce18]">
                      <a href={`/invoice/${t.invoice_no}`} target="_blank" rel="noreferrer">{t.invoice_no}</a>
                    </td>
                    <td>
                      <div className="text-white/80">{t.purpose || "—"}</div>
                      <div className="text-xs text-white/40">{t.branch}</div>
                    </td>
                    <td className="font-medium">{formatINR(t.amount)}</td>
                    <td><span className={statusBadgeClass(t.status)}>{t.status}</span></td>
                    <td className="text-xs text-white/50">{formatDate(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
